import React, { createContext, useContext, useEffect, useState } from 'react';
import { getMe } from '../features/auth/services/authApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [state, setState] = useState({
        status: 'loading',
        user: null,
        permissions: [],
        noAccess: false,
        noAccessReason: null,
        authMeError: false,
        loyalpassAdmin: false,
    });

    useEffect(() => {
        getMe()
            .then((res) => {
                const data = res.data;
                if (data?.noAccess) {
                    setState({
                        status: 'ready',
                        user: null,
                        permissions: [],
                        noAccess: true,
                        noAccessReason: data.noAccessReason || 'access_removed',
                        authMeError: false,
                        loyalpassAdmin: false,
                    });
                } else {
                    setState({
                        status: 'ready',
                        user: data,
                        permissions: data?.permissions || ['full_access'],
                        noAccess: false,
                        noAccessReason: null,
                        authMeError: false,
                        loyalpassAdmin: !!data?.loyalpassAdmin,
                    });
                }
            })
            .catch(() => {
                setState({
                    status: 'ready',
                    user: null,
                    permissions: [],
                    noAccess: true,
                    noAccessReason: 'api_error',
                    authMeError: true,
                    loyalpassAdmin: false,
                });
            });
    }, []);

    const hasPermission = (key) => {
        if (state.loyalpassAdmin) {
            return key === 'loyalpass_admin';
        }
        if (!state.permissions?.length) {
            return false;
        }
        return state.permissions.includes('full_access') || state.permissions.includes(key);
    };

    const value = {
        ...state,
        hasPermission,
        loading: state.status === 'loading'
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook for components inside the merchant dashboard. Returns the resolved
 * merchant user, permissions, and a `hasPermission` helper.
 *
 * Note: there is also a `useAuth` hook in `features/auth/hooks/useAuth` that
 * deals with Redux-backed session tokens (login/logout/etc). Always import
 * THIS hook by its full name to avoid that ambiguity.
 */
export function useMerchantAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useMerchantAuth must be used within AuthProvider');
    }
    return ctx;
}
